import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppDataProvider } from './context/AppDataContext';
import { authEvents } from './services/authEvents';
import SessionExpiryModal from './components/auth/SessionExpiryModal';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Brands from './pages/Brands';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import Tenders from './pages/Tenders';
import TenderDetail from './pages/TenderDetail';
import TenderAwardees from './pages/TenderAwardees';
import TenderAwardeeDetail from './pages/TenderAwardeeDetail';
import Orders from './pages/Orders';
import KYC from './pages/KYC';
import CSVImport from './pages/CSVImport';
import Settings from './pages/Settings';
import Roles from './pages/Roles';
import Permissions from './pages/Permissions';
import Activities from './pages/Activities';
import DataCallReport from './pages/DataCallReport';
import TenderCallReport from './pages/TenderCallReport';
import CustomerCallReport from './pages/CustomerCallReport';
import './App.css';

const PrivateRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const AuthRedirectHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    return authEvents.onUnauthorized(() => {
      if (location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }
    });
  }, [navigate, location.pathname]);

  return null;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <SessionExpiryModal />
        <AppDataProvider>
          <AuthRedirectHandler />
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/users"
            element={
              <PrivateRoute>
                <Users />
              </PrivateRoute>
            }
          />
          <Route
            path="/brands"
            element={
              <PrivateRoute>
                <Brands />
              </PrivateRoute>
            }
          />
          <Route
            path="/tender-awardees"
            element={
              <PrivateRoute>
                <TenderAwardees />
              </PrivateRoute>
            }
          />
          <Route
            path="/tender-awardees/:id"
            element={
              <PrivateRoute>
                <TenderAwardeeDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <PrivateRoute>
                <Customers />
              </PrivateRoute>
            }
          />
          <Route
            path="/customers/:id"
            element={
              <PrivateRoute>
                <CustomerDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/leads"
            element={
              <PrivateRoute>
                <Leads />
              </PrivateRoute>
            }
          />
          <Route
            path="/leads/:id"
            element={
              <PrivateRoute>
                <LeadDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/tenders"
            element={
              <PrivateRoute>
                <Tenders />
              </PrivateRoute>
            }
          />
          <Route
            path="/tenders/:id"
            element={
              <PrivateRoute>
                <TenderDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <PrivateRoute>
                <Orders />
              </PrivateRoute>
            }
          />
          <Route
            path="/kyc"
            element={
              <PrivateRoute>
                <KYC />
              </PrivateRoute>
            }
          />
          <Route
            path="/csv-import"
            element={
              <PrivateRoute>
                <CSVImport />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Settings />
              </PrivateRoute>
            }
          />
          <Route
            path="/roles"
            element={
              <PrivateRoute>
                <Roles />
              </PrivateRoute>
            }
          />
          <Route
            path="/permissions"
            element={
              <PrivateRoute>
                <Permissions />
              </PrivateRoute>
            }
          />
          <Route
            path="/activities"
            element={
              <PrivateRoute>
                <Activities />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports/data-calls"
            element={
              <PrivateRoute>
                <DataCallReport />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports/tender-calls"
            element={
              <PrivateRoute>
                <TenderCallReport />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports/customer-calls"
            element={
              <PrivateRoute>
                <CustomerCallReport />
              </PrivateRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </AppDataProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
